import {
  buildCharacterCardsPromptBlock,
  migrateCreativeToCharacterCards,
  normalizeCharacterCards,
} from './comicCharacters'
import type {
  ComicArtStylePreset,
  ComicProjectCreative,
  ComicTonePreset,
} from './comicDomainTypes'
import { normalizeCharacterReferenceUrls } from './comicReferenceLimits'
import { requireValidComicPanelAspect } from './comicPanelAspectUi'

export type {
  ComicArtStylePreset,
  ComicCharacterCard,
  ComicProjectCreative,
  ComicTonePreset,
} from './comicDomainTypes'

export const COMIC_ART_STYLE_OPTIONS: { value: ComicArtStylePreset; label: string; hint: string }[] = [
  { value: 'japanese', label: '日系漫画', hint: '线稿清晰、分镜感' },
  { value: 'korean', label: '韩漫', hint: '精致上色、竖条感' },
  { value: 'american', label: '美漫', hint: '硬朗轮廓、动感' },
  { value: 'watercolor', label: '水彩插画', hint: '柔和晕染' },
  { value: 'semi_realistic', label: '半写实', hint: '细节丰富' },
  { value: 'custom', label: '自定义', hint: '在下方补充' },
]

export const COMIC_TONE_OPTIONS: { value: ComicTonePreset; label: string; hint: string }[] = [
  { value: 'light', label: '轻松', hint: '节奏明快、冲突轻量，适合日常、喜剧与轻冒险。' },
  { value: 'serious', label: '严肃', hint: '叙事克制、情绪沉稳，适合悬疑、剧情向和现实题材。' },
  { value: 'dark', label: '黑暗', hint: '压迫感更强，光影和剧情冲突更重，适合惊悚或黑色题材。' },
  { value: 'healing', label: '治愈', hint: '温暖柔和、节奏舒缓，强调陪伴感、日常感和情绪修复。' },
  { value: 'epic', label: '史诗', hint: '场面宏大、叙事张力更强，适合奇幻、战争或长篇冒险。' },
  { value: 'custom', label: '自定义', hint: '手动描述整部作品的叙事气质。' },
]

const ART_STYLE_PROMPT: Record<Exclude<ComicArtStylePreset, 'custom'>, string> = {
  japanese: '日系漫画风格，线稿清晰，动漫赛璐璐上色',
  korean: '韩漫风格，精致数字上色，人物五官细腻',
  american: '美式漫画风格，硬朗轮廓，动感构图',
  watercolor: '水彩插画风格，柔和笔触与晕染',
  semi_realistic: '半写实插画，细节丰富，光影自然',
}

const TONE_PROMPT: Record<Exclude<ComicTonePreset, 'custom'>, string> = {
  light: '整体氛围轻松明快',
  serious: '氛围严肃沉稳',
  dark: '氛围偏暗压抑，戏剧张力强',
  healing: '氛围温暖治愈',
  epic: '氛围宏大史诗感',
}

function clip(raw: unknown, max: number): string {
  const s = typeof raw === 'string' ? raw.trim() : ''
  return s.length > max ? s.slice(0, max) : s
}

function normalizePreset<T extends string>(raw: unknown, allowed: T[], fallback: T): T {
  const v = typeof raw === 'string' ? raw.trim() : ''
  return (allowed.includes(v as T) ? v : fallback) as T
}

function readPanelAspect(raw: unknown): string {
  return typeof raw === 'string' ? raw.trim() : ''
}

export function normalizeComicProjectCreative(raw: unknown): ComicProjectCreative {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
  return {
    panelAspect: readPanelAspect(o.panelAspect),
    artStyle: normalizePreset(
      o.artStyle,
      COMIC_ART_STYLE_OPTIONS.map((x) => x.value),
      'japanese',
    ),
    artStyleNotes: clip(o.artStyleNotes, 400),
    tone: normalizePreset(o.tone, COMIC_TONE_OPTIONS.map((x) => x.value), 'light'),
    toneNotes: clip(o.toneNotes, 300),
    colorPalette: clip(o.colorPalette, 200),
    worldSetting: clip(o.worldSetting, 400),
    characterNotes: clip(o.characterNotes, 800),
    characterReferenceUrls: normalizeCharacterReferenceUrls(o.characterReferenceUrls),
    characters: migrateCreativeToCharacterCards({
      characterNotes: clip(o.characterNotes, 800),
      characterReferenceUrls: normalizeCharacterReferenceUrls(o.characterReferenceUrls),
      characters: normalizeCharacterCards(o.characters),
    }),
    avoidNotes: clip(o.avoidNotes, 400),
  }
}

export function buildDefaultComicProjectCreative(): ComicProjectCreative {
  return normalizeComicProjectCreative({ panelAspect: '' })
}

export function validateComicProjectCreative(
  creative: ComicProjectCreative,
): { ok: true } | { ok: false; message: string } {
  const aspect = requireValidComicPanelAspect(creative.panelAspect)
  if (!aspect.ok) return aspect
  return { ok: true }
}

export type ComicCreativeChangeWarning = {
  field: 'panelAspect' | 'artStyle' | 'tone'
  message: string
}

export function warningsForCreativeChange(
  prev: ComicProjectCreative,
  next: ComicProjectCreative,
  hasImages: boolean,
): ComicCreativeChangeWarning[] {
  if (!hasImages) return []
  const out: ComicCreativeChangeWarning[] = []
  if (prev.panelAspect !== next.panelAspect) {
    out.push({
      field: 'panelAspect',
      message: '画册中已有成图，修改单页比例后画布预览会与旧图不一致。',
    })
  }
  return out
}

export function creativeHasContent(creative: ComicProjectCreative): boolean {
  return Boolean(
    creative.panelAspect ||
      creative.artStyleNotes ||
      creative.toneNotes ||
      creative.colorPalette ||
      creative.worldSetting ||
      creative.characterNotes ||
      creative.characters.length > 0 ||
      creative.characterReferenceUrls.length > 0 ||
      creative.avoidNotes ||
      creative.artStyle !== 'japanese' ||
      creative.tone !== 'light',
  )
}

/** 拼入整页生图描述前缀（无内容时返回空串） */
export function buildCreativeStylePrompt(creative: ComicProjectCreative): string {
  const lines: string[] = []
  if (creative.artStyle !== 'custom') {
    lines.push(ART_STYLE_PROMPT[creative.artStyle])
  }
  if (creative.artStyleNotes) lines.push(`画风：${creative.artStyleNotes}`)
  if (creative.tone !== 'custom') {
    lines.push(TONE_PROMPT[creative.tone])
  }
  if (creative.toneNotes) lines.push(`叙事基调：${creative.toneNotes}`)
  if (creative.colorPalette) lines.push(`色调与光影：${creative.colorPalette}`)
  if (creative.worldSetting) lines.push(`世界观/场景时代：${creative.worldSetting}`)
  const charBlock = buildCharacterCardsPromptBlock(creative.characters)
  if (charBlock) lines.push(charBlock)
  else if (creative.characterNotes) lines.push(`角色一致性：${creative.characterNotes}`)
  if (creative.avoidNotes) lines.push(`请避免：${creative.avoidNotes}`)
  if (lines.length === 0) return ''
  return ['【作品视觉基调】', ...lines].join('\n')
}
